import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAuth } from '../hooks/useAuth';
import {
  createAtm,
  deleteAtm,
  getAtm,
  getPublicAtms,
  getSharedAtm,
  getUserAtms,
  processSharedAtmTransaction,
  shareAtm,
  updateAtm,
} from '../services/databaseIntegration';
import {
  Atm,
  AtmListItem,
  AtmShareSettings,
  CreateAtmDto,
  SharedAtm,
  SharedAtmTransaction,
} from '../types/atm';

interface AtmContextType {
  // ATM management
  userAtms: AtmListItem[];
  publicAtms: AtmListItem[];
  loadingUserAtms: boolean;
  loadingPublicAtms: boolean;
  fetchUserAtms: () => Promise<void>;
  fetchPublicAtms: () => Promise<void>;
  createNewAtm: (atmData: CreateAtmDto) => Promise<string>;
  fetchAtm: (atmId: string, includeShareSettings?: boolean) => Promise<Atm | null>;
  updateExistingAtm: (atmId: string, atmData: Partial<Atm>) => Promise<void>;
  deleteExistingAtm: (atmId: string) => Promise<void>;

  // Sharing
  shareExistingAtm: (atmId: string, settings: AtmShareSettings) => Promise<string>;
  fetchSharedAtm: (shareId: string) => Promise<SharedAtm | null>;
  processTransaction: (shareId: string, transaction: SharedAtmTransaction) => Promise<void>;

  // Error handling
  error: string | null;
  clearError: () => void;
}

const AtmContext = createContext<AtmContextType | undefined>(undefined);

export const AtmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();

  // State
  const [userAtms, setUserAtms] = useState<AtmListItem[]>([]);
  const [publicAtms, setPublicAtms] = useState<AtmListItem[]>([]);
  const [loadingUserAtms, setLoadingUserAtms] = useState<boolean>(false);
  const [loadingPublicAtms, setLoadingPublicAtms] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's ATMs
  const fetchUserAtms = async (): Promise<void> => {
    try {
      setLoadingUserAtms(true);
      setError(null);

      const atmsList = await getUserAtms();
      setUserAtms(atmsList);
    } catch (err) {
      console.error('Error fetching user ATMs:', err);
      setError('Failed to load your ATMs');
    } finally {
      setLoadingUserAtms(false);
    }
  };

  // Fetch public ATMs
  const fetchPublicAtms = async (): Promise<void> => {
    try {
      setLoadingPublicAtms(true);
      setError(null);

      const atmsList = await getPublicAtms(20);
      setPublicAtms(atmsList);
    } catch (err) {
      console.error('Error fetching public ATMs:', err);
      setError('Failed to load public ATMs');
    } finally {
      setLoadingPublicAtms(false);
    }
  };

  // Create a new ATM
  const createNewAtm = async (atmData: CreateAtmDto): Promise<string> => {
    try {
      setError(null);
      console.log('🏗️ AtmContext: Creating ATM with data:', atmData);

      const atmId = await createAtm(atmData);
      console.log('✨ AtmContext: ATM created successfully with ID:', atmId);

      // Refresh user ATMs list
      console.log('🔄 AtmContext: Refreshing user ATMs list...');
      await fetchUserAtms();
      console.log('✅ AtmContext: User ATMs refreshed');

      return atmId;
    } catch (err) {
      console.error('💥 AtmContext: Error creating ATM:', err);
      setError('Failed to create ATM');
      throw err;
    }
  };

  // Fetch a specific ATM
  const fetchAtm = async (atmId: string, includeShareSettings = false): Promise<Atm | null> => {
    try {
      setError(null);
      return await getAtm(atmId, includeShareSettings);
    } catch (err) {
      console.error('Error fetching ATM:', err);
      setError('Failed to load ATM details');
      throw err;
    }
  };

  // Update an existing ATM
  const updateExistingAtm = async (atmId: string, atmData: Partial<Atm>): Promise<void> => {
    try {
      setError(null);
      await updateAtm(atmId, atmData);

      // Refresh user ATMs list
      await fetchUserAtms();
    } catch (err) {
      console.error('Error updating ATM:', err);
      setError('Failed to update ATM');
      throw err;
    }
  };

  // Delete an existing ATM
  const deleteExistingAtm = async (atmId: string): Promise<void> => {
    try {
      setError(null);
      await deleteAtm(atmId);

      // Refresh user ATMs list
      await fetchUserAtms();
    } catch (err) {
      console.error('Error deleting ATM:', err);
      setError('Failed to delete ATM');
      throw err;
    }
  };

  // Share an existing ATM
  const shareExistingAtm = async (atmId: string, settings: AtmShareSettings): Promise<string> => {
    try {
      setError(null);
      return await shareAtm(atmId, settings);
    } catch (err) {
      console.error('Error sharing ATM:', err);
      setError('Failed to share ATM');
      throw err;
    }
  };

  // Fetch a shared ATM
  const fetchSharedAtm = async (shareId: string): Promise<SharedAtm | null> => {
    try {
      setError(null);
      return await getSharedAtm(shareId);
    } catch (err) {
      console.error('Error fetching shared ATM:', err);
      setError('Failed to load shared ATM');
      throw err;
    }
  };

  // Process a transaction on a shared ATM
  const processTransaction = async (
    shareId: string,
    transaction: SharedAtmTransaction
  ): Promise<void> => {
    try {
      setError(null);
      await processSharedAtmTransaction(shareId, transaction);
    } catch (err) {
      console.error('Error processing transaction:', err);
      setError('Failed to process transaction');
      throw err;
    }
  };

  // Clear error
  const clearError = (): void => {
    setError(null);
  };

  // Initial loading of ATMs when the component mounts
  useEffect(() => {
    fetchUserAtms();
    fetchPublicAtms();
  }, []);

  // Refresh ATMs when user changes
  useEffect(() => {
    if (currentUser) {
      fetchUserAtms();
    }
  }, [currentUser]);

  const value: AtmContextType = {
    userAtms,
    publicAtms,
    loadingUserAtms,
    loadingPublicAtms,
    fetchUserAtms,
    fetchPublicAtms,
    createNewAtm,
    fetchAtm,
    updateExistingAtm,
    deleteExistingAtm,
    shareExistingAtm,
    fetchSharedAtm,
    processTransaction,
    error,
    clearError,
  };

  return <AtmContext.Provider value={value}>{children}</AtmContext.Provider>;
};

// Hook for using the ATM context
export const useAtm = (): AtmContextType => {
  const context = useContext(AtmContext);

  if (context === undefined) {
    throw new Error('useAtm must be used within an AtmProvider');
  }

  return context;
};
